import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, SettingsService } from 'src/app/services/settings.service';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { Device } from '@ionic-native/device/ngx';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { share, skip } from 'rxjs/operators';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { TenantAuthService } from '../services/tenant-auth.service';
import { TerminalDataService } from '../private/terminal/services/terminal-data.service';
import { TerminalStoreService } from '../private/terminal/services/terminal-store.service';
import { DEVICE_WS_COMMAND_PROTOCOL } from './pos-command.models';

declare class SocketResponse {
  method: string;
  data: any;
}

@Injectable({ providedIn: 'root' })
export class DeviceWsConnector {
  private _hubConnection: HubConnection;
  private onMethodBs: BehaviorSubject<SocketResponse | null> =
    new BehaviorSubject<SocketResponse | null>(null);
  private onMethodBs$: Observable<SocketResponse | null> = this.onMethodBs
    .asObservable()
    .pipe(skip(1), share());

  private _methodSubjects = new Map<string, Subject<any>>();
  private _registeredHubMethods = new Set<string>();
  // Server calls that expect a result back (SignalR client results), one handler per method.
  private _resultHandlers = new Map<string, (...args: any[]) => any>();
  private _registeredResultMethods = new Set<string>();
  private _storeChangedSubscribed = false;
  private _tenantAuthSubscribed = false;
  private _startInProgress = false;
  private _restartRequested = false;
  private _reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private _reconnectAttempt = 0;
  private readonly _reconnectMinDelayMs = 5_000;
  private readonly _reconnectBaseDelayMs = 10_000;
  private readonly _reconnectMaxDelayMs = 60_000;
  private readonly _deviceInfoStorageKey = 'device_ws_device_info';
  private readonly _deviceInfoResendMs = 24 * 60 * 60 * 1000;

  constructor(
    private settings: SettingsService,
    private _device: Device,
    @Inject('ENVIRONMENT') private environment: any,
    private _appVersion: AppVersion,
    private _tenantAuthService: TenantAuthService,
    private _terminalData: TerminalDataService,
    private _terminalStore: TerminalStoreService,
  ) {}

  // Connected, Connecting, Reconnecting, Disconnected or Disconnecting; 'NotStarted' before the first connect.
  get connectionState(): string {
    return this._hubConnection?.state ?? 'NotStarted';
  }

  async connect() {
    if (this._hubConnection?.state == HubConnectionState.Connected) {
      return;
    }

    await this._start();
  }

  private async _reconnect() {
    if (this._startInProgress) {
      this._restartRequested = true;
      return;
    }

    await this._start(true);
  }

  private _scheduleReconnect() {
    if (
      this._reconnectTimer != null ||
      this._startInProgress ||
      this._hubConnection?.state == HubConnectionState.Connected
    ) {
      return;
    }

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = undefined;
      void this._start();
    }, this._nextReconnectDelayMs());
  }

  // Exponential backoff with random spread, so terminals don't all reconnect at once after a server restart.
  private _nextReconnectDelayMs() {
    const cap = Math.min(
      this._reconnectMaxDelayMs,
      this._reconnectBaseDelayMs * 2 ** this._reconnectAttempt,
    );
    this._reconnectAttempt++;

    return Math.max(this._reconnectMinDelayMs, Math.random() * cap);
  }

  private _clearReconnectTimer() {
    if (this._reconnectTimer != null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }
  }

  private async _stopHubQuietly() {
    if (!this._hubConnection) {
      return;
    }

    this._hubConnection.onclose(() => {});
    try {
      await this._hubConnection.stop();
    } catch {
      // ignore stop errors while replacing the connection
    }
  }

  private async _start(force = false) {
    if (this._startInProgress) {
      this._restartRequested = true;
      return;
    }

    if (
      !force &&
      this._hubConnection?.state == HubConnectionState.Connected
    ) {
      return;
    }

    this._startInProgress = true;
    this._clearReconnectTimer();
    this._registeredHubMethods.clear();

    await this._stopHubQuietly();

    const hubUrl = this.settings.getValue(APP_CONFIG.POS_HUB_ENDPOINT);

    const storeId = this._getErpStoreId();
    const storeIdParam =
      storeId != null && !Number.isNaN(storeId) ? `&storeId=${storeId}` : '';
    // cmdv=2: this app answers PosCommandRequest with a result (see PosTerminalCommunicationService).
    const url = `${hubUrl}?deviceId=${this._device.uuid}${storeIdParam}&cmdv=${DEVICE_WS_COMMAND_PROTOCOL}`;
    this._hubConnection = new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => this._tenantAuthService.tenantId,
        transport: HttpTransportType.WebSockets,
      })
      // .withAutomaticReconnect([10000, 20000, 30000, 20000, 20000])
      .build();

    // Before start, so a command sent right after connecting already finds its handler.
    this._registeredResultMethods.clear();
    for (const method of this._resultHandlers.keys()) {
      this._registerResultHandler(method);
    }

    this._hubConnection.onclose(() => {
      this._scheduleReconnect();
    });

    this._hubConnection.onreconnected(() => {
      console.log('device-ws onreconnected');
    });

    this._hubConnection.onreconnecting(() => {
      console.log('device-ws onreconnecting');
    });

    let startFailed = false;
    try {
      await this._hubConnection.start();
      this._handleWebSocketConnected();
    } catch (err) {
      console.error(`Device WS start error.`, err);
      startFailed = true;
    } finally {
      this._startInProgress = false;
      if (this._restartRequested) {
        this._restartRequested = false;
        void this._reconnect();
      } else if (startFailed) {
        // Scheduled only now: _scheduleReconnect skips while a start is in progress.
        this._scheduleReconnect();
      }
    }
  }

  // No app-level heartbeat: SignalR keep-alive pings detect dead connections, and the server treats an open connection as online.
  private _handleWebSocketConnected() {
    this._reconnectAttempt = 0;
    this._ensureAuthAndStoreSubscriptions();
    this._registerHubMethods();
    void this._updateDeviceInfo();
  }

  private _ensureAuthAndStoreSubscriptions() {
    if (!this._tenantAuthSubscribed) {
      this._tenantAuthService.onTenantAuth.subscribe(() => {
        void this._reconnect();
      });
      this._tenantAuthSubscribed = true;
    }

    if (!this._storeChangedSubscribed) {
      this._terminalStore.onStoreChanged.subscribe(() => {
        void this._reconnect();
      });
      this._storeChangedSubscribed = true;
    }
  }

  on(method: string): Observable<any> {
    if (!this._methodSubjects.has(method)) {
      this._methodSubjects.set(method, new Subject<any>());
    }

    if (this._hubConnection) {
      this._registerHubMethod(method);
    }

    return this._methodSubjects.get(method).asObservable();
  }

  // Registers the handler for a server call that waits for a result: what the handler returns
  // (or the error it throws) goes back to the server. A later call replaces the handler.
  handle(method: string, handler: (...args: any[]) => any) {
    this._resultHandlers.set(method, handler);
    if (this._hubConnection) {
      this._registerResultHandler(method);
    }
  }

  private _registerResultHandler(method: string) {
    if (!this._hubConnection || this._registeredResultMethods.has(method)) {
      return;
    }

    // SignalR rejects a call that more than one handler returns a result for.
    this._registeredResultMethods.add(method);
    this._hubConnection.on(method, (...args: any[]) =>
      this._resultHandlers.get(method)?.(...args),
    );
  }

  private _registerHubMethods() {
    for (const method of this._methodSubjects.keys()) {
      this._registerHubMethod(method);
    }
  }

  private _registerHubMethod(method: string) {
    if (!this._hubConnection || this._registeredHubMethods.has(method)) {
      return;
    }

    this._registeredHubMethods.add(method);
    this._hubConnection.on(method, (data) => {
      this._methodSubjects.get(method)?.next(data);

      const resp: SocketResponse = {
        method,
        data,
      };

      this.onMethodBs.next(resp);
    });
  }

  // Sent only when the info changed or once a day, not on every reconnect.
  private async _updateDeviceInfo() {
    try {
      const model = {
        uuid: this._device.uuid,
        platform: this._device.platform,
        version: this._device.version,
        model: this._device.model,
        manufacturer: this._device.manufacturer,
        serial: this._device.serial,
        IsVirtual: this._device.isVirtual.toString(),
        appVersion: this.environment.version,
      };

      if (this._device.platform != 'web_browser') {
        model.appVersion = await this._appVersion.getVersionNumber();
      }

      // The device row is per tenant, so a tenant switch sends it again.
      const info = JSON.stringify({
        tenantId: this._tenantAuthService.tenantId,
        model,
      });
      const sent = this._readSentDeviceInfo();
      if (
        sent?.info === info &&
        Date.now() - sent.sentAt < this._deviceInfoResendMs
      ) {
        return;
      }

      if (this._hubConnection?.state != HubConnectionState.Connected) {
        return;
      }

      await this._hubConnection.invoke('DeviceInfo', model);
      this._writeSentDeviceInfo({ info, sentAt: Date.now() });
    } catch (err) {
      console.error('Device WS device info error.', err);
    }
  }

  private _readSentDeviceInfo(): { info: string; sentAt: number } | null {
    try {
      return JSON.parse(localStorage.getItem(this._deviceInfoStorageKey));
    } catch {
      return null;
    }
  }

  private _writeSentDeviceInfo(value: { info: string; sentAt: number }) {
    try {
      localStorage.setItem(this._deviceInfoStorageKey, JSON.stringify(value));
    } catch {
      // without storage it is sent on every connect, as before
    }
  }

  async invoke(method: string): Promise<any> {
    if (this._hubConnection.state == HubConnectionState.Connected) {
      await this._hubConnection.invoke(method);
    } else {
      throw 'Not connected';
    }
  }

  async invokeWithData(method: string, data: any): Promise<any> {
    if (this._hubConnection.state == HubConnectionState.Connected) {
      await this._hubConnection.invoke(method, data);
    }
  }

  // Like invokeWithData, but fails when there is no connection instead of doing nothing.
  async invokeOrFail(method: string, ...args: any[]): Promise<any> {
    if (this._hubConnection?.state != HubConnectionState.Connected) {
      throw new Error(`No connection to the server (${this.connectionState})`);
    }

    return this._hubConnection.invoke(method, ...args);
  }

  private _getErpStoreId(): number | null {
    const settings = this._terminalData.terminalSettings();
    if (!settings?.store) {
      return null;
    }

    const selectedStoreId = this._terminalStore.getTerminalStoreId();
    const store = selectedStoreId
      ? this._findStoreById(settings.store, selectedStoreId)
      : settings.store;

    if (!store) {
      return null;
    }

    return store.erpStoreId != null
      ? Number(store.erpStoreId)
      : Number(store.id);
  }

  private _findStoreById(store: any, storeId: number): any {
    if (Number(store.id) === Number(storeId)) {
      return store;
    }

    return (store.departments || []).find(
      (department: any) => Number(department.id) === Number(storeId),
    );
  }
}
